/**
 * MyAssignmentsDashboard — landing page at /projects/me.
 * Default for external users; optional view for internal.
 *
 * Shows: this-week summary card · assigned segments list · recent activity.
 */
import { useState } from 'react';
import {
  Box, Typography, Paper, Stack, Button, Chip, IconButton, Divider, Skeleton,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotesIcon from '@mui/icons-material/Notes';
import { useNavigate } from 'react-router-dom';
import { useMyAssignments } from '../../hooks/useProjects';
import LogHoursDialog from './LogHoursDialog';

function shortLabel(name) {
  if (!name) return '';
  const idx = name.indexOf('\u2014');
  return idx >= 0 ? name.slice(idx + 1).trim() : name;
}

export default function MyAssignmentsDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useMyAssignments();
  const [logOpen, setLogOpen] = useState(false);
  const [logSegmentId, setLogSegmentId] = useState(null);

  if (isLoading) {
    return <Box sx={{ p: 3 }}><Skeleton variant="rectangular" height={120} /></Box>;
  }

  const segments = (data?.assignments || []).filter((a) => a.kind === 'segment');
  const allowedIds = segments.map((s) => s.id);

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>My Staycation</Typography>

      {/* This week */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="caption" color="text.secondary">This week</Typography>
        <Stack direction="row" spacing={{ xs: 2, sm: 4 }} alignItems="baseline" sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {(data?.week_total_hours || 0).toFixed(1)}h
            </Typography>
            <Typography variant="caption" color="text.secondary">logged</Typography>
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums' }}>{segments.length}</Typography>
            <Typography variant="caption" color="text.secondary">active segments</Typography>
          </Box>
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
          <Button
            variant="contained"
            startIcon={<AccessTimeIcon />}
            onClick={() => { setLogSegmentId(null); setLogOpen(true); }}
          >
            Log hours
          </Button>
        </Stack>
      </Paper>

      {/* Assignments */}
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Your assignments</Typography>
      {segments.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            You're not assigned to any segments yet. Ask an admin to invite you.
          </Typography>
        </Paper>
      )}
      <Stack spacing={1}>
        {segments.map((s) => (
          <Paper key={s.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {shortLabel(s.name)}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  {s.project_role && <Chip size="small" label={s.project_role} />}
                  {s.hourly_rate != null && (
                    <Typography variant="caption" color="text.secondary">
                      ${Number(s.hourly_rate).toFixed(2)}/hr
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {(s.hours_logged || 0).toFixed(1)}h logged
                  </Typography>
                </Stack>
              </Box>
              <Button
                size="small"
                startIcon={<AccessTimeIcon />}
                onClick={() => { setLogSegmentId(s.id); setLogOpen(true); }}
              >
                Log
              </Button>
              <Button
                size="small"
                startIcon={<NotesIcon />}
                onClick={() => navigate(`/projects?id=${s.id}`)}
              >
                Notes
              </Button>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <LogHoursDialog
        open={logOpen}
        onClose={() => setLogOpen(false)}
        defaultProjectId={logSegmentId}
        restrictToProjectIds={allowedIds}
      />
    </Box>
  );
}
